import type { MarketTrade } from '../../../api/useMarketTrades';
import { shortAccount } from '../../../utils/traderFormat';
import type { ChartSeries, SeriesMarker } from '../seriesTypes';

function isNoSide(trade: MarketTrade): boolean {
    return trade.outcomeIndex === 1;
}

function yesPrice(trade: MarketTrade): number {
    return isNoSide(trade) ? 1 - trade.price : trade.price;
}

function isBullish(trade: MarketTrade): boolean {
    return (trade.side === 'buy') !== isNoSide(trade);
}

function compact(n: number): string {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return n.toFixed(0);
}

export function tradeMarkerSeries(
    tradesByWallet: ReadonlyMap<string, readonly MarketTrade[]>,
    colors: ReadonlyMap<string, string>
): ChartSeries[] {
    const result: ChartSeries[] = [];
    for (const [wallet, trades] of tradesByWallet) {
        const color = colors.get(wallet) ?? '#888888';
        const maxQuote = Math.max(1, ...trades.map(t => t.quoteAmount));
        const markers: SeriesMarker[] = trades.map(t => ({
            id: `${wallet}:${t.id}`,
            t: t.timestamp.getTime(),
            v: yesPrice(t),
            color,
            shape: isBullish(t) ? 'up' : 'down',
            title: `${t.side === 'buy' ? 'Buy' : 'Sell'} ${compact(t.amount)} ${
                isNoSide(t) ? 'NO' : 'YES'
            } at ${(t.price * 100).toFixed(1)}¢`,
            detail: `$${compact(t.quoteAmount)} · ${shortAccount(wallet)}`,
            weight: t.quoteAmount / maxQuote,
        }));
        result.push({
            id: `trades:${wallet}`,
            label: `Trades ${shortAccount(wallet)}`,
            color,
            unit: 'probability',
            draw: 'markers',
            axis: 'hidden',
            home: 'probability',
            movable: false,
            hasData: markers.length > 0,
            data: { kind: 'markers', markers },
        });
    }
    return result;
}
